import { hasNonMagicAction } from '../../../shared/rules/fighter.mjs';
import apiFetch from '../../../utils/apiFetch';

const request = async (campaignId, operation, payload) => {
  if (process.env.NODE_ENV === 'development' && operation === 'cleave-attack') {
    console.debug('[Cleave] authorization request started', {
      combatantId: payload?.combatantId, primaryTargetId: payload?.primaryTargetId,
      secondaryTargetId: payload?.secondaryTargetId, parentResolutionId: payload?.parentResolutionId,
    });
  }
  const response = await apiFetch(`/campaigns/${encodeURIComponent(campaignId)}/combat/action-economy/${operation}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  const result = await response.json();
  if (process.env.NODE_ENV === 'development' && operation === 'cleave-attack') {
    console.debug('[Cleave] authorization response', { status: response.status, result: result?.status, reason: result?.reason });
  }
  if (!response.ok && !result.status) {
    const error = new Error(result.reason || result.message || response.statusText || 'Action economy could not be updated.');
    error.status = response.status;
    error.reason = result.reason || result.message || null;
    error.payload = result;
    throw error;
  }
  return result;
};

export const commitDirectSpellHit = (campaignId, payload) => request(campaignId, 'spell-hit', payload);
export const spendTurnResource = (campaignId, payload) => request(campaignId, 'spend', payload);
export const commitOrdinaryAttack = (campaignId, payload) => request(campaignId, 'attack', payload);
export const commitLightExtraAttack = (campaignId, payload) => request(campaignId, 'light-attack', payload);
export const commitCleaveExtraAttack = (campaignId, payload) => request(campaignId, 'cleave-attack', payload);
export const commitHordeBreakerAttack = (campaignId, payload) => request(campaignId, 'horde-breaker-attack', payload);
export const commitCunningAction = (campaignId, payload) => request(campaignId, 'cunning-action', payload);
export const commitSteadyAim = (campaignId, payload) => request(campaignId, 'steady-aim', payload);
export const consumeSteadyAimAttack = (campaignId, payload) => request(campaignId, 'steady-aim/consume', payload);
export const resolveCunningActionHide = (campaignId, payload) => request(campaignId, 'cunning-action/hide-result', payload);

export const commitWeaponAttack = (campaignId, actionEconomy, payload) => {
  if (actionEconomy?.kind === 'light-extra') {
    return commitLightExtraAttack(campaignId, { ...payload, attackActionId: actionEconomy.attackActionId });
  }
  if (actionEconomy?.kind === 'ordinary') {
    return commitOrdinaryAttack(campaignId, { ...payload, light: actionEconomy.light === true });
  }
  if (actionEconomy?.kind === 'cleave-extra') {
    return commitCleaveExtraAttack(campaignId, { ...payload, ...actionEconomy.authorization });
  }
  if (actionEconomy?.kind === 'horde-breaker') {
    // The secondary target is in the authorization; Horde Breaker records it as attacked itself.
    const { targetId, ...rest } = payload;
    return commitHordeBreakerAttack(campaignId, { ...rest, ...actionEconomy.authorization });
  }
  throw new Error('The weapon attack action-economy source is invalid.');
};

export const currentTurnLedger = (combat, combatantId) => {
  const ledger = combat?.turnActionLedger;
  return ledger?.combatantId === combatantId && ledger?.turnSequence === combat?.turnSequence ? ledger : null;
};

export const isStructuredTurnForCombatant = (combat, combatantId) => {
  const active = combat?.participants?.[combat?.activeTurn];
  const activeId = String(active?.characterId || active?.enemyId || '');
  return Boolean(combatantId)
    && Number.isInteger(combat?.turnSequence)
    && activeId === String(combatantId);
};

export const projectTurnActionSlots = (usedSlots, combat, combatantId) => {
  if (!isStructuredTurnForCombatant(combat, combatantId)) return usedSlots;
  const ledger = currentTurnLedger(combat, String(combatantId));
  return {
    ...usedSlots,
    action: { ...(usedSlots?.action || {}), 0: ledger?.actionSpent ? 'used' : 'active', ...(ledger?.actionSurge ? { 1: ledger.actionSurge.spent ? 'used' : 'active' } : {}) },
    bonus: { ...(usedSlots?.bonus || {}), 0: ledger?.bonusActionSpent ? 'used' : 'active' },
  };
};

export const reconcileTurnActionLedger = (combat, ledger) => {
  if (!ledger || !isStructuredTurnForCombatant(combat, ledger.combatantId)) return combat;
  if (ledger.turnSequence !== combat.turnSequence) return combat;
  return { ...combat, turnActionLedger: ledger };
};

export const canSpendTurnResource = (combat, combatantId, resource) => {
  const ledger = currentTurnLedger(combat, combatantId);
  return !ledger || !ledger[`${resource}Spent`];
};

